// crossSellingThemeManager.ts
import { CSSProperties } from "react";
import { PartialBrandTheme, PartialQuestThemeData } from "./CrossSellingType";
import { hexToGlassColor } from "../../../../general";

interface CrossSellingStyleConfig {
  Form?: CSSProperties;
  Heading?: CSSProperties;
  Description?: CSSProperties;
  Label?: CSSProperties;
  Input?: CSSProperties;
  EmailError?: {
    text?: string;
    errorStyle?: CSSProperties;
  };
  Timer?: {
    backgroundColor?: string;
    primaryColor?: string;
    secondaryColor?: string;
    TimerCard?: CSSProperties;
  };
  PrimaryButton?: CSSProperties;
  SecondaryButton?: CSSProperties;
  BackgroundWrapper?: CSSProperties;
  Footer?: {
    FooterStyle?: CSSProperties;
    FooterTextStyle?: CSSProperties;
    FooterIcon?: CSSProperties;
    FooterText?: string;
  };
}

interface CrossSellingThemeConfig {
  backgroundColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  buttonColor?: string;
  borderColor?: string;
  fontFamily?: string;
}

class CrossSellingThemeManager {
  themeConfig: CrossSellingThemeConfig;
  styleConfig: CrossSellingStyleConfig;
  questThemeData: PartialQuestThemeData;
  brandTheme: PartialBrandTheme;

  constructor(
    themeConfig: CrossSellingThemeConfig = {},
    styleConfig: CrossSellingStyleConfig = {},
    questThemeData: PartialQuestThemeData = {
      accentColor: "",
      theme: "",
      borderRadius: "",
      buttonColor: "",
      images: [],
    },
    brandTheme: PartialBrandTheme = {
      accentColor: "",
      background: "",
      borderRadius: "",
      primaryButton: "",
      contentColor: "",
      fontFamily: "",
      logo: "",
      primaryColor: "",
      secondaryColor: "",
      tertiaryColor: "",
      titleColor: "",
    }
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = questThemeData;
    this.brandTheme = brandTheme;
  }

  getMainContentStyles(): CSSProperties {
    return {
      background: 
        this.styleConfig?.Form?.backgroundColor ||
        this.themeConfig?.backgroundColor,
      height: this.styleConfig?.Form?.height || "auto",
      fontFamily: this.themeConfig.fontFamily || "'Figtree', sans-serif",
      borderRadius:
        this.styleConfig?.Form?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius,
      ...this.styleConfig?.Form,
    };
  }

  getHeadingStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Heading,
    };
  }

  getDescriptionStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Description?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor,
      ...this.styleConfig?.Description,
    };
  }

  getGradientStyles() {
    return {
      wrapper: {
        background: "white",
        borderRadius:
          this.styleConfig?.Form?.borderRadius ||
          this.questThemeData?.borderRadius ||
          this.brandTheme?.borderRadius ||
          "10px",
        width: 'fit-content'
      },
      background: {
        fontFamily: this.themeConfig.fontFamily || "'Figtree', sans-serif",
        borderRadius:
          this.styleConfig?.BackgroundWrapper?.borderRadius ||
          this.questThemeData?.borderRadius ||
          this.brandTheme?.borderRadius,
        background: this.styleConfig?.BackgroundWrapper?.background || !(this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? 'linear-gradient(13deg, #fff 38.24%, #9035ff 59.39%, #0065ff 70.7%)' : (this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.includes("gradient") ? (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) : `linear-gradient(13deg, rgba(255, 255, 255, 0) 38.24%, ${hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.6)} 59.39%, ${this.brandTheme?.primaryColor || this.questThemeData?.accentColor} 70.7%)`,
        ...this.styleConfig?.BackgroundWrapper,
      },
      heading: {
        color: this.styleConfig?.Heading?.color || 'white',
      },
      description: {
        color:
          this.styleConfig?.Description?.color ||
          "white",
        ...this.styleConfig?.Description,
      },
    };
  }

  getTimerStyles() {
    return {
      card: {
        background: this.styleConfig?.Timer?.backgroundColor,
        ...this.styleConfig?.Timer?.TimerCard,
      },
      primaryText: {
        color:
          this.styleConfig?.Timer?.primaryColor ||
          this.brandTheme?.titleColor,
        fontFamily: "Figtree"
      },
      secondaryText: {
        color:
          this.styleConfig?.Timer?.secondaryColor ||
          this.brandTheme?.contentColor,
        fontFamily: "Figtree"
      },
    };
  }

  getInputStyles() {
    return {
      label: this.styleConfig?.Label || { color: this.brandTheme?.contentColor },
      input: this.styleConfig?.Input || { color: this.brandTheme?.titleColor },
      error: {
        text: this.styleConfig?.EmailError?.text || "This is not a valid email",
        style: this.styleConfig?.EmailError?.errorStyle || {},
      },
    };
  }

  getPrimaryButtonStyles(): CSSProperties {
    return {
      background:
        this.styleConfig?.PrimaryButton?.background ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor ||
        this.themeConfig?.buttonColor,
      borderRadius: this.styleConfig?.PrimaryButton?.borderRadius || this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px",
      ...this.styleConfig?.PrimaryButton,
    };
  }

  getSecondaryButtonStyles(): CSSProperties {
    return {
      borderColor:
        this.styleConfig?.SecondaryButton?.borderColor ||
        this.themeConfig?.borderColor,
      backgroundColor:
        this.styleConfig?.SecondaryButton?.backgroundColor ||
        this.themeConfig?.backgroundColor,
      color:
        this.styleConfig?.SecondaryButton?.color ||
        this.themeConfig?.primaryColor,
      fontFamily: this.themeConfig.fontFamily || "'Figtree', sans-serif",
      borderRadius: this.styleConfig?.SecondaryButton?.borderRadius || this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px",
      ...this.styleConfig?.SecondaryButton,
    };
  }

  getFooterStyles() {
    return {
      container: {
        background:
          this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
          this.styleConfig?.Form?.backgroundColor ||
          this.styleConfig?.Form?.background ||
          this.themeConfig?.backgroundColor,
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...this.styleConfig?.Footer?.FooterStyle,
      },
      text: this.styleConfig?.Footer?.FooterTextStyle || {},
      icon: this.styleConfig?.Footer?.FooterIcon || {},
    };
  }

  updateThemeConfig(newConfig: Partial<CrossSellingThemeConfig>): void {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<CrossSellingStyleConfig>): void {
    this.styleConfig = {
      ...this.styleConfig,
      ...newConfig,
      Footer: {
        ...this.styleConfig?.Footer,
        ...newConfig?.Footer,
      },
      Timer: {
        ...this.styleConfig?.Timer,
        ...newConfig?.Timer,
      },
    };
  }

  updateQuestTheme(newTheme: Partial<PartialQuestThemeData>): void {
    this.questThemeData = {
      ...this.questThemeData,
      ...newTheme,
    };
  }

  updateBrandTheme(newTheme: Partial<PartialBrandTheme>): void {
    this.brandTheme = {
      ...this.brandTheme,
      ...newTheme,
    };
  }

  getIconColor() {
    return {
      primary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 1) : "#9035FF",
      secondary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.5) : "#BF8AFF",
      tertiary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.1) : "#F4EBFF",
    }
  }
}

export default CrossSellingThemeManager;